import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Globe, Search, FileText, Users, Zap } from 'lucide-react';
import { format } from 'date-fns';

interface OnboardingMessage {
  id: string;
  workflowId?: string;
  type: string;
  message: string;
  timestamp: string;
  url?: string;
  agent?: string;
  agentName?: string;
  agentType?: string;
  summary?: any;
  result?: any;
  topics?: any[];
  error?: string;
  step?: string;
}

export default function OnboardingBotMessagePage() {
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // For now, use mock data since the Insights Hub WebSocket isn't running
  useEffect(() => {
    setMessages([
      {
        id: '1',
        type: 'onboarding:started',
        message: 'Welcome to the Onboarding Agent! This agent orchestrates the complete site onboarding workflow.',
        timestamp: new Date().toISOString(),
        agent: 'onboarding'
      },
      {
        id: '2',
        type: 'info',
        message: 'When sites are onboarded, their progress will appear here in real-time.',
        timestamp: new Date().toISOString(),
        agent: 'onboarding'
      }
    ]);
    setIsConnected(true);
  }, []);

  // Use messages directly (no filtering needed since we're showing onboarding messages only)
  const onboardingMessages = messages;

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'onboarding:started':
        return <Globe className="h-4 w-4" />;
      case 'step:started':
        return <Zap className="h-4 w-4" />;
      case 'crawler:complete':
        return <Search className="h-4 w-4" />;
      case 'semantic:complete':
        return <FileText className="h-4 w-4" />;
      case 'adjacent:complete':
        return <Users className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getMessageVariant = (type: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (type.includes('error')) return 'destructive';
    if (type.includes('complete')) return 'default';
    if (type.includes('started')) return 'secondary';
    return 'outline';
  };

  return (
    <div className="container mx-auto p-6">
      <div className="space-y-6">
        {/* Header Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src="/crew-agents/onboarding-agent.png" />
                  <AvatarFallback>OB</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle>OnboardingBot</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Orchestrates the complete site onboarding workflow
                  </p>
                </div>
              </div>
              <Badge variant={isConnected ? 'default' : 'secondary'}>
                {isConnected ? 'Connected' : 'Disconnected'}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle>Onboarding Activities</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : onboardingMessages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No onboarding activities yet</p>
                <p className="text-sm mt-1">
                  Onboarding messages will appear here when sites are being onboarded
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  {onboardingMessages.map((message) => (
                    <div
                      key={message.id}
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          {getMessageIcon(message.type)}
                          <Badge variant={getMessageVariant(message.type)}>
                            {message.type}
                          </Badge>
                          {message.workflowId && (
                            <span className="text-xs text-muted-foreground">
                              Workflow: {message.workflowId.slice(0, 8)}...
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(message.timestamp), 'HH:mm:ss')}
                        </span>
                      </div>

                      <p className="text-sm">{message.message}</p>

                      {message.url && (
                        <div className="text-xs text-muted-foreground">
                          Site: {message.url}
                        </div>
                      )}

                      {message.step && (
                        <div className="text-xs text-muted-foreground">
                          Step: {message.step}
                        </div>
                      )}

                      {message.summary && (
                        <div className="mt-2 p-2 bg-muted rounded text-xs">
                          <div className="font-medium mb-1">Summary:</div>
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(message.summary, null, 2)}
                          </pre>
                        </div>
                      )}

                      {message.topics && message.topics.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium mb-1">Generated Topics:</div>
                          <div className="flex flex-wrap gap-1">
                            {message.topics.map((topic: any, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {topic.topic || topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {message.error && (
                        <div className="mt-2 p-2 bg-destructive/10 text-destructive rounded text-xs">
                          Error: {message.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}